/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-11-18 17:53:32
 * @LastEditors: Jack Deng
 * @LastEditTime: 2022-05-01 23:02:04
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelContentEditor/Components/TimeOrderedList/index.tsx
 */
import { DragNDrop, ItemWrapper, LineCollapse } from '@/src/components';
import CountWords from '@/src/components/CountWords/CountWords';
import { SectionContainer } from '@/src/components/Layouts';
import ErrorProcessingComponent from '@/src/constrains/SalesChannelPreviewPageComponents/ErrorProcessingHOC/ErrorProcessingComponent';
import { randomNumber } from '@/src/tools/helper';
import { PlusOutlined } from '@ant-design/icons';
import { default as classNames, default as classname } from 'classnames';
import { get } from 'lodash';
import { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, Input } from 'src/components/Antd';
import { useI18n } from 'src/hooks/useRouterChange';
import css from 'src/pages/SalesChannelContentEditor/SalesChannelEditorHeader/style.module.less';
import {
  i18nGet,
  generateMomentTime,
  handleOnGetCurrentSCTimezone,
  handleOnGetCurrentSCTimezoneUTC,
  handleOnFormatCurrentSCTimeAndDate,
} from 'src/tools/tools';
import TimerPickerModal from '../../Components/TimePickerModal';
import styles from './style.less';
import { processingList } from './tool';

type Props = {
  title?: string;
  timeZone?: string;
  buttonText?: string;
  droppableId?: string;
  dragDisabled?: boolean;
  hideName?: boolean;
  hideTimeEdit?: boolean;
  hideTimeZone?: boolean;
  hideAddButton?: boolean;
  orderedByTime?: boolean;
  titlePropertyName?: string;
  isResponsive?: boolean;
  timePropertyName?: {
    startTime: string;
    startDate: string;
    endTime: string;
    endDate: string;
  };
  getErrorProcessingComponentProps?: (item: any, isNew?: boolean) => any;
  activeType?: CollapseType;
  pendingType?: CollapseType;
  previousType?: CollapseType;
  lineCollapseOpenGroup?: LineCollapseStatus[];
  onAddNewItem?: () => void;
  onChangeLineCollapse?: (keys: LineCollapseStatus[]) => void;
  onDragSorting?: (
    { oldIndex, newIndex }: any,
    { wOldIndex, wNewIndex }: any,
  ) => void;
  customOrderFunc?: (list: Array<any>) => any;
  renderSingleChild?: (item: any, isNew: boolean, disabled: boolean) => any;
  onChangeTime?: (
    item: any,
    isNew: boolean,
    start: Moment,
    end: Moment,
  ) => void;
  onChangeItemName?: (item: any, isNew: boolean, value: any) => void;
  list: (() => Array<any>) | Array<any>;
  newItem: any;
};

export enum LineCollapseStatus {
  Active = 'Active',
  Pending = 'Pending',
  Previous = 'Previous',
}

export enum CollapseType {
  Draggable = 'Draggable',
  Normal = 'Normal',
  Disabled = 'Disabled',
}

// const FORMAT = 'DD MMM YYYY HH:mm';

function TimeOrderedList({
  list,
  newItem,
  title,
  timeZone,
  droppableId,
  dragDisabled,
  buttonText,
  hideName,
  isResponsive,
  hideTimeEdit,
  hideTimeZone,
  hideAddButton,
  orderedByTime = true,
  titlePropertyName,
  timePropertyName = {
    startTime: 'beginTime',
    startDate: 'beginDate',
    endTime: 'endTime',
    endDate: 'endDate',
  },
  activeType,
  pendingType,
  previousType,
  customOrderFunc,
  onDragSorting,
  getErrorProcessingComponentProps,
  lineCollapseOpenGroup,
  renderSingleChild,
  onAddNewItem,
  onChangeTime,
  onChangeItemName,
  onChangeLineCollapse,
}: Props) {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const scTimezoneUTC = handleOnGetCurrentSCTimezone();
  const scTimezoneNum = handleOnGetCurrentSCTimezoneUTC();
  const id = randomNumber();
  const [timeVisible, setTimeVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const wholeList = typeof list === 'function' ? list() : list;
  const processedList = processingList(
    orderedByTime,
    timePropertyName,
    customOrderFunc,
    wholeList,
    scTimezoneUTC,
  ); //改起来麻烦

  const start = generateMomentTime(
    currentItem?.item ? currentItem?.item[timePropertyName.startDate] : '',
    currentItem?.item ? currentItem?.item[timePropertyName.startTime] : '',
    scTimezoneUTC,
  );

  const end = generateMomentTime(
    currentItem?.item ? currentItem?.item[timePropertyName.endDate] : '',
    currentItem?.item ? currentItem?.item[timePropertyName.endTime] : '',
    scTimezoneUTC,
  );

  const [group, setGroup] = useState(
    () =>
      lineCollapseOpenGroup || [
        LineCollapseStatus.Active,
        LineCollapseStatus.Pending,
        LineCollapseStatus.Previous,
      ],
  );

  const handleOpenGroup = (keys: Array<any>) => {
    if (onChangeLineCollapse) {
      onChangeLineCollapse(keys);
    } else {
      setGroup(keys);
    }
  };

  const handleOrderOnChange = (
    newIndex: number,
    oldIndex: number,
    nIndexInWholeList?: number,
    oIndexInWholeList?: number,
  ) => {
    if (onDragSorting) {
      onDragSorting(
        { newIndex, oldIndex },
        {
          newIndex: nIndexInWholeList,
          oldIndex: oIndexInWholeList,
        },
      );
    }
  };

  useEffect(() => {
    setGroup(
      lineCollapseOpenGroup || [
        LineCollapseStatus.Active,
        LineCollapseStatus.Pending,
        LineCollapseStatus.Previous,
      ],
    );
  }, [lineCollapseOpenGroup]);

  const renderSettings = (
    status: LineCollapseStatus,
    type: CollapseType,
    list: Array<any>,
  ) => {
    switch (status) {
      case LineCollapseStatus.Active: {
        return renderGroup(type, list, LineCollapseStatus.Active);
      }
      case LineCollapseStatus.Pending: {
        return renderGroup(type, list, LineCollapseStatus.Pending);
      }
      case LineCollapseStatus.Previous: {
        return renderGroup(type, list, LineCollapseStatus.Previous);
      }
    }
  };

  const renderCollapseChild = (
    errorProcessingProps: any,
    item: any,
    isNew?: boolean,
    disabled?: boolean,
  ) => {
    const start = generateMomentTime(
      item[timePropertyName.startDate],
      item[timePropertyName.startTime],
      scTimezoneUTC,
    );
    const end = generateMomentTime(
      item[timePropertyName.endDate],
      item[timePropertyName.endTime],
      scTimezoneUTC,
    );

    return (
      <div className={classNames(styles.wrapper, { [css.item]: isResponsive })}>
        {!hideName && (
          <SectionContainer size={'small'} bottomBorder>
            <ItemWrapper
              size={'small'}
              title={getI18n('salesChannelContent.index.nameforinternaluse')}
              rightPart={<CountWords allCount={100} count={50} />}>
              <Input
                disabled={disabled}
                error={
                  errorProcessingProps.specificError['name'] &&
                  errorProcessingProps.specificError['name']?.length !== 0
                }
                help={errorProcessingProps.specificError['name']}
                size={'large'}
                value={item[titlePropertyName]}
                onChange={
                  onChangeItemName
                    ? onChangeItemName.bind(null, item, !!isNew)
                    : null
                }
              />
            </ItemWrapper>
          </SectionContainer>
        )}
        {orderedByTime && !hideTimeEdit && (
          <>
            <SectionContainer
              className={styles.paddingBottom}
              size={'small'}
              bottomBorder>
              <div className={styles.timeContainer}>
                <div className={styles.time}>
                  <div className={styles.timeTitle}>{`${getI18n(
                    'general.startTime',
                  )} (UTC ${scTimezoneNum})`}</div>
                  <div className={styles.timeContent}>
                    {start.isValid()
                      ? handleOnFormatCurrentSCTimeAndDate(start)
                      : '-'}
                  </div>
                </div>
                <div className={styles.time}>
                  <div className={styles.timeTitle}>
                    {`${getI18n('general.endTime')} (UTC ${scTimezoneNum})`}
                  </div>
                  <div className={styles.timeContent}>
                    {end.isValid()
                      ? handleOnFormatCurrentSCTimeAndDate(end)
                      : '-'}
                  </div>
                </div>
                <div className={styles.timeEditor}>
                  {!disabled && (
                    <div className={styles.editorText}>
                      <a
                        onClick={() => {
                          setCurrentItem({ item, isNew });
                          setTimeVisible(true);
                        }}>
                        {getI18n(
                          'salesChannelContent.index.editStartandEndTime',
                        )}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </SectionContainer>
          </>
        )}
        {renderSingleChild(item, isNew, disabled)}
      </div>
    );
  };

  const renderDragChld = (_d, provided, index) => {
    return (
      <div {...provided.dragHandleProps}>
        <ErrorProcessingComponent
          {...getErrorProcessingComponentProps(_d)}
          title={
            orderedByTime ? null : getErrorProcessingComponentProps(_d)?.title
          }
          collapseProps={{ titleContainerClassName: styles.draggableContainer }}
          renderTitle={
            orderedByTime
              ? (errorProcessingProps) => (
                  <>
                    {renderTitle(
                      errorProcessingProps,
                      CollapseType.Draggable,
                      _d,
                    )}
                  </>
                )
              : null
          }
          renderComponent={(errorProcessingProps) =>
            renderCollapseChild(errorProcessingProps, _d)
          }
        />
      </div>
    );
  };

  const renderTitle = (
    errorProcessingProps: any,
    type: CollapseType,
    item: any,
    isNew?: boolean,
  ) => {
    const start = generateMomentTime(
      item[timePropertyName.startDate],
      item[timePropertyName.startTime],
      scTimezoneUTC,
    );
    const end = generateMomentTime(
      item[timePropertyName.endDate],
      item[timePropertyName.endTime],
      scTimezoneUTC,
    );

    const error = [
      ...(errorProcessingProps?.specificError?.beginTime || []),
      ...(errorProcessingProps?.specificError?.endTime || []),
    ];
    return (
      <div className={styles.titleContainer}>
        <div
          className={classname(styles.disabled, {
            [styles.green]: type !== CollapseType.Disabled,
            [styles.error]: error.length !== 0,
          })}>
          {error.length !== 0
            ? error[0]
            : isNew
            ? getI18n('salesChannelContent.index.pleasesetstarttimeandendtime')
            : `${handleOnFormatCurrentSCTimeAndDate(
                start,
              )} - ${handleOnFormatCurrentSCTimeAndDate(end)}`}
        </div>
        <div className={styles.titleText}>{item[titlePropertyName]}</div>
      </div>
    );
  };

  const renderGroup = (
    type: CollapseType,
    list: Array<any>,
    status: LineCollapseStatus,
  ) => {
    switch (type) {
      case CollapseType.Draggable: {
        const _list = list.map((v) => ({ ...v, draggableId: v.uuid }));
        return (
          <div className={isResponsive ? styles.list : undefined}>
            <DragNDrop
              droppableId={droppableId || id}
              disableAllDrags={dragDisabled}
              list={_list}
              childGenerator={renderDragChld}
              onChange={(o, n) => {
                const oUuid = get(_list, `${o}.uuid`);
                const nUuid = get(_list, `${n}.uuid`);
                const oIndexInWholeList = wholeList.findIndex(
                  (i) => i.uuid === oUuid,
                );
                const nIndexInWholeList = wholeList.findIndex(
                  (i) => i.uuid === nUuid,
                );

                handleOrderOnChange(o, n, oIndexInWholeList, nIndexInWholeList);
              }}
            />
          </div>
        );
      }
      case CollapseType.Disabled: {
        return (
          <div className={isResponsive ? styles.list : undefined}>
            {list.map((item: any, index: number) => {
              return (
                <ErrorProcessingComponent
                  {...getErrorProcessingComponentProps(item)}
                  title={
                    orderedByTime
                      ? null
                      : getErrorProcessingComponentProps(item)?.title
                  }
                  renderTitle={
                    orderedByTime
                      ? (errorProcessingProps) => (
                          <>
                            {renderTitle(
                              errorProcessingProps,
                              CollapseType.Disabled,
                              item,
                            )}
                          </>
                        )
                      : null
                  }
                  renderComponent={(errorProcessingProps) =>
                    renderCollapseChild(errorProcessingProps, item, false, true)
                  }
                />
              );
            })}
          </div>
        );
      }
      case CollapseType.Normal: {
        return (
          <div className={isResponsive ? styles.list : undefined}>
            {list.map((item: any, index: number) => {
              return (
                <ErrorProcessingComponent
                  {...getErrorProcessingComponentProps(item)}
                  title={
                    orderedByTime
                      ? null
                      : getErrorProcessingComponentProps(item)?.title
                  }
                  renderTitle={
                    orderedByTime
                      ? (errorProcessingProps) => (
                          <>
                            {renderTitle(
                              errorProcessingProps,
                              CollapseType.Normal,
                              item,
                            )}
                          </>
                        )
                      : null
                  }
                  renderComponent={(errorProcessingProps) =>
                    renderCollapseChild(errorProcessingProps, item)
                  }
                />
              );
            })}
          </div>
        );
      }
    }
  };

  const List = [
    {
      headerText: getI18n('general.status.active'),
      key: LineCollapseStatus.Active,
      content: renderSettings(
        LineCollapseStatus.Active,
        activeType || CollapseType.Draggable,
        processedList[LineCollapseStatus.Active],
      ),
      hideStatusText: true,
    },
    {
      headerText: getI18n('general.paymentStatus.pending'),
      key: LineCollapseStatus.Pending,
      content: renderSettings(
        LineCollapseStatus.Pending,
        pendingType || CollapseType.Normal,
        processedList[LineCollapseStatus.Pending],
      ),
      // hideStatusText: true,
    },
    {
      headerText: getI18n('salesChannelContent.index.previous'),
      key: LineCollapseStatus.Previous,
      content: renderSettings(
        LineCollapseStatus.Previous,
        previousType || CollapseType.Disabled,
        processedList[LineCollapseStatus.Previous],
      ),
      // hideStatusText: true,
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.title}>{title}</div>
      {!hideTimeZone && (
        <div className={styles.subTitle}>{`${getI18n(
          'general.timeZone',
        )} (UTC ${scTimezoneNum})`}</div>
      )}
      {!hideAddButton && (
        <div className={styles.add}>
          <Button
            size={'large'}
            type={'primary'}
            block
            className={styles.button}
            icon={<PlusOutlined rev className={styles.icon} />}
            onClick={onAddNewItem}>
            {buttonText}
          </Button>
        </div>
      )}
      {Object.values(newItem).length !== 0 && (
        <ErrorProcessingComponent
          {...getErrorProcessingComponentProps(newItem, true)}
          title={
            orderedByTime
              ? null
              : getErrorProcessingComponentProps(newItem)?.title
          }
          renderTitle={
            orderedByTime
              ? (errorProcessingProps) => (
                  <>
                    {renderTitle(
                      errorProcessingProps,
                      CollapseType.Normal,
                      newItem,
                      true,
                    )}
                  </>
                )
              : null
          }
          renderComponent={(errorProcessingProps) =>
            renderCollapseChild(errorProcessingProps, newItem, true)
          }
        />
      )}
      <div className={styles.content}>
        <LineCollapse open={group} setOpen={handleOpenGroup} data={List} />
      </div>
      <TimerPickerModal
        hideTitle
        visible={timeVisible}
        onCancel={() => setTimeVisible(false)}
        onSubmit={(start, end) => {
          onChangeTime(currentItem?.item, currentItem?.isNew, start, end);
          setTimeVisible(false);
        }}
        afterClose={() => setCurrentItem(null)}
        occupiedRanges={[]}
        timezoneGmt={timeZone}
        date={{
          startTime: start.isValid() ? start.format('YYYY-MM-DD HH:mm') : null,
          endTime: end.isValid() ? end.format('YYYY-MM-DD HH:mm') : null,
        }}
      />
    </div>
  );
}

export default TimeOrderedList;
